import React, { PureComponent } from 'react';
import styled from 'styled-components';
import plotly from './../node_modules/plotly.js/dist/plotly.js';
import createPlotlyComponent from 'react-plotly.js/factory';
import * as csDictionary from './../node_modules/plotly.js/lib/locales/de.js';
import ComponentTitle from './ComponentTitle';
import UpdatedDate from './UpdatedDate';
import { normalizeBarTooltips } from './normalizeBarTooltips';

const StyledPlotComponent = styled.div`
  grid-column: span 12;
  @media (min-width: 1280px) {
    grid-column: span ${props => props.columns};
  }
  @media print {
    page-break-inside: avoid;
    margin-bottom: 30px;
    display: block;
  }
`;

const StyledPlotWrapper = styled.div`
  border-radius: 5px;
  background: white;
  padding: 10px 10px 35px 10px;
  position: relative;
  box-shadow: 0 2px 4px 0 rgba(38, 47, 51, 0.3);
  overflow: hidden;

  @media print {
    box-shadow: none !important;
  }
`;

class PlotComponent extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      data: [],
      layout: {},
      frames: [],
      config: {},
      revision: 0,
      showTotals: true,
      downloadSelection: []
    };
    this.TOTALNAME = 'Gesamt';

    this.handleSelectionUpdate = this.handleSelectionUpdate.bind(this);
    this.handleCSVClick = this.handleCSVClick.bind(this);
  }

  // componentDidCatch(error, info) {
  //   // Display fallback UI
  //   this.setState({ hasError: true });
  //   // You can also log the error to an error reporting service
  //   console.log(error, info);
  // }

  // Lifecycle Methods
  // https://developmentarc.gitbooks.io/react-indepth/content/life_cycle/the_life_cycle_recap.html
  componentWillMount() {
    plotly.register(csDictionary);
    this.initalizeConfig();
    this.initializeChart();
  }

  componentDidMount() {
    // console.log('componentDidMount');
  }

  // Do not do stuff in componentWillUpdate. Instead use onRestyle onRelayout because state.data does have strange behaviour here. Might be due to plotly caching and the usage of fullData/fullLayout.
  componentWillUpdate() {
    // console.log('componentWillUpdate');
  }

  componentDidUpdate() {
    // console.log('componentDidUpdate');
  }

  // Event Handling
  // https://github.com/plotly/react-plotly.js/blob/master/README.md#state-management
  // Errors are thrown at various steps, but plot seems to work correctly
  // https://github.com/plotly/react-plotly.js/issues/52
  handleUpdate(figure) {
    // console.log('handleUpdate');
    if (this.props.showAsTimeseries) {
      this.updateTotalTrace(this.state.data, 'MONTH');
      this.updateTotalTrace(this.state.data, 'CHANGE');
      this.updateTotalTrace(this.state.data, 'YEAR');
      this.hideTotalBasedOnRange();
      this.styleLayout(this.state.layout);
      this.setState({
        revision: this.state.revision + 1
      });
    }
  }

  UNSAFE_componentWillUpdate() {
    // console.log('UNSAFE_componentWillUpdate');
  }

  // Clicking on update menus
  // Not sure why update menues does not trigger handleUpdate
  handleButtonClicked(e) {
    // console.log('handleButtonClicked');
    if (this.props.showAsTimeseries) {
      this.hideTotalBasedOnRange();
      this.styleLayout(this.state.layout);
    }
    this.setState({
      revision: this.state.revision + 1
    });
  }

  handleRelayout(e) {
    // console.log('handleRelayout');
  }

  handleClick(e) {
    console.log('handleClick');
    console.log(e);
    // console.log(e.points[0].fullData);
    console.log(this.state);
    this.setState({
      downloadSelection: {
        x: e.points[0].x,
        y: e.points[0].y,
        name: e.points[0].fullData.name,
        legendgroup: e.points[0].fullData.legendgroup
      }
    });
  }

  handleSelectionUpdate(selection) {
    this.setState({
      downloadSelection: selection
    });
  }

  // Util functions
  monthDiff(d1, d2) {
    let months;
    months = (d2.getFullYear() - d1.getFullYear()) * 12;
    months -= d1.getMonth() + 1;
    months += d2.getMonth();
    return months <= 0 ? 0 : months;
  }

  // Function to only get unique elements in an array
  onlyUnique(value, index, self) {
    return self.indexOf(value) === index;
  }

  // Find the index of the biggest element
  indexOfMax(arr) {
    if (arr.length === 0) {
      return -1;
    }
    var max = arr[0];
    var maxIndex = 0;
    for (var i = 1; i < arr.length; i++) {
      if (arr[i] > max) {
        maxIndex = i;
        max = arr[i];
      }
    }
    return maxIndex;
  }

  // TraceGroup Management
  // Get all traces belonging to a specific group. Done through legendgroup.
  getTraceGroup(traces, groupname) {
    let tracegroup = [];
    for (let i = 0; i < traces.length; i++) {
      if (
        traces[i].legendgroup.includes(groupname) &&
        !traces[i].legendgroup.includes(this.TOTALNAME)
      ) {
        tracegroup.push(traces[i]);
      }
    }
    return tracegroup;
  }

  getCurrentVisibleTraceGroup() {
    let traces = this.state.data;
    let currentTraceGroup = 'none';
    for (let i = 0; i < traces.length; i++) {
      if (traces[i].visible) {
        currentTraceGroup = traces[i].legendgroup.split(':')[1];
      }
    }
    return currentTraceGroup;
  }

  showTraces(traces) {
    for (let i = 0; i < traces.length; i++) {
      traces[i].visible = true;
    }
  }

  hideTraces(traces) {
    for (let i = 0; i < traces.length; i++) {
      traces[i].visible = false;
    }
  }

  // Converting and Enriching Traces
  // Used to get the correct y Values to show in bar charts and not scatter
  extractYValues(text) {
    let nString = text.split('N: ')[1];
    if (nString) {
      return nString.replace(/\s/g, '');
    }
    return undefined;
  }

  // Currently R provides traces with too many x values
  cleanTraces(traces) {
    for (let i = 0; i < traces.length; i++) {
      let n = this.indexOfMax(traces[i].x) + 1;
      let xvals = traces[i].x.slice(0, n);
      let yvals = traces[i].y.slice(0, n);
      let text = traces[i].text.slice(0, n);
      traces[i].x = xvals;
      traces[i].y = yvals;
      traces[i].text = text;
    }
    return traces; // Check if necessary or if input is altered
  }

  // Plotly requires x values for timeseries to be in the format YY-MM-DD
  // TODO make sure data is exported consistently
  convertToPlotlyFriendlyXValues(traces) {
    for (let i = 0; i < traces.length; i++) {
      let xvals = traces[i].x;
      let dates = xvals.map(date => {
        let dateOriginal = new Date(date * 24 * 3600 * 1000);
        // Mapping everything to the first of the month
        // Some data is provided as the last day of the month
        let dateString =
          dateOriginal.getFullYear() +
          '-' +
          ('0' + (dateOriginal.getMonth() + 1)).slice(-2) +
          '-01';
        return dateString;
        //new Date(date * 24 * 3600 * 1000).toISOString().split('T')[0]
      });
      if (dates) {
        traces[i].x = dates;
      }
    }
    return traces; // Check if necessary or if input is altered
  }

  // Currently R provides traces that are stacked
  // Doing a hack and getting the absolute values out of the tooltips
  convertToAbsolutTraces(traces) {
    for (let i = 0; i < traces.length; i++) {
      let toolTips = traces[i].text;
      if (Array.isArray(toolTips)) {
        traces[i].y = toolTips.map(this.extractYValues);
      }
    }
    return traces; // Check if necessary or if input is altered
  }

  // Doing a hack and grouping traces with legendgroup and group name MONTH, CHANGE, YEAR
  convertToMonthlyTraces(traces) {
    for (let i = 0; i < traces.length; i++) {
      traces[i].legendgroup = traces[i].legendgroup + ':MONTH';
    }
    return traces; // Check if necessary or if input is altered
  }

  // Calculating changes and updating the tooltip
  generateChangeTraces(traces) {
    for (let i = 0; i < traces.length; i++) {
      let yValues = traces[i].y.map(this.parseTraceY);
      let changeValues = [];
      let toolTips = traces[i].text;
      for (let j = 1; j < yValues.length; j++) {
        changeValues[j] = String(
          this.refineTraceY(yValues[j] - yValues[j - 1])
        );
        if (Array.isArray(toolTips)) {
          toolTips[j] = this.updateTooltip(
            toolTips[j],
            changeValues[j],
            yValues[j]
          );
        }
      }
      changeValues[0] = NaN;
      traces[i].y = changeValues;
      traces[i].text = toolTips;
      traces[i].legendgroup = traces[i].legendgroup + ':CHANGE';
    }
    return traces; // Check if necessary or if input is altered
  }

  // Calculating yearly traces
  generateYearlyTraces(traces, containsOverallValues = false) {
    const firstY = (traces.length > 0 && traces[0].y.length > 0) ? traces[0].y[0] : '';
    const isPercent = (typeof firstY === 'string' && firstY.includes('%'));

    for (let i = 0; i < traces.length; i++) {
      let yValues = traces[i].y;
      let yearlyY = [];
      let yearlyX = [];
      let yearlyTooltip = [];
      let sum = 0;
      let sumMonthCount = 0;

      for (let j = 0; j < yValues.length; j++) {
        let xval = traces[i].x[j];
        if (
          (typeof xval === 'string' || xval instanceof String) &&
          xval.includes('-')
        ) {
          if (
            !containsOverallValues &&
            !(typeof traces[i].x[j + 1] === 'undefined') &&
            traces[i].x[j].split('-')[0] === traces[i].x[j + 1].split('-')[0]
          ) {
            if (yValues[j]) {
              sum = sum + this.parseTraceY(yValues[j]);
              sumMonthCount++;
            }
          } else if (
            containsOverallValues &&
            (traces[i].x[j].split('-')[1] != 12 &&
              !(typeof traces[i].x[j + 1] === 'undefined'))
          ) {
            //Intentionally empty
          } else {
            if (yValues[j]) {
              sum = sum + this.parseTraceY(yValues[j]);
            }
            if (!containsOverallValues) {
              sumMonthCount++;
              if (isPercent) {
                sum = sum / sumMonthCount;
              }
            }
            sum = this.refineTraceY(sum);

            yearlyTooltip.push(
              this.updateTooltipYearly(
                traces[i].text[j],
                sum,
                yearlyY[yearlyY.length - 1]
              )
            ); //TODO correct tooltip

            yearlyY.push(sum);
            yearlyX.push(traces[i].x[j].split('-')[0]);
            sum = 0;
            sumMonthCount = 0;
          }
        }
      }
      traces[i].x = yearlyX;
      traces[i].y = yearlyY;
      traces[i].text = yearlyTooltip;
      traces[i].legendgroup = traces[i].legendgroup + ':YEAR';
    }
    return traces;
  }

  // Used to update tooltip for changes
  updateTooltip(text, value, oldvalue) {
    //let nString = text.split('N: ')[1];
    if (text) {
      //let newTooltip = text.replace(/N: /g, 'N: ' + value);
      let newTooltip =
        text +
        '<br />Veränderung: ' +
        value +
        ' (' +
        this.refineTraceY((value * 100) / oldvalue) +
        '%)';
      return newTooltip;
    }
    return undefined;
  }

  //Used to update tooltip for changes
  updateTooltipYearly(text, value, oldvalue) {
    if (text && text.split('Datum')[0] && !isNaN(value)) {
      let newTooltip = text.split('Datum')[0] + 'N: ' + value;
      if (!isNaN(oldvalue)) {
        newTooltip =
          newTooltip +
          '<br />Veränderung: ' +
          this.refineTraceY(value - oldvalue) +
          ' (' +
          this.refineTraceY(((value - oldvalue) * 100) / oldvalue) +
          '%)';
      }
      return newTooltip;
    }
    return undefined;
  }

  // generate a trace that show the sum of all traces
  // Used to show above staced charts
  generateTotalTrace(traces) {
    // Calculate Annotations Monthly
    let totaly = [];
    let totaltext = [];

    for (let i = 0; i < traces[0].x.length; i++) {
      let ysum = 0;
      for (let t = 0; t < traces.length; t++) {
        if (
          !typeof traces[t].visible === 'undefined' &&
          traces[t].visible === 'legendonly'
        ) {
          // intentionally empty
        } else if (
          typeof traces[t].visible === 'undefined' ||
          traces[t].visible !== 'legendonly'
        ) {
          // Deal with NA values
          let yvalue = this.parseTraceY(traces[t].y[i]);
          ysum = ysum + yvalue;
        } else {
          // intentionally empty
        }
      }
      ysum = this.refineTraceY(ysum);

      totaly[i] = ysum;
      totaltext[i] = ysum.toString();
    }
    let totalx = traces[0].x;

    let totaltrace = {};
    totaltrace.x = totalx;
    totaltrace.y = totaly;
    totaltrace.text = totaltext;
    totaltrace.name = this.TOTALNAME;
    totaltrace.legendgroup = '';
    if (traces[0].legendgroup.includes(':')) {
      totaltrace.legendgroup = ':' + traces[0].legendgroup.split(':')[1];
    }
    return totaltrace;
  }

  parseTraceY(y) {
    return parseFloat(y) || 0;
  }

  refineTraceY(y) {
    if (this.isFloat(y)) {
      return y.toFixed(2);
    }
    return y;
  }

  isFloat(value) {
    return typeof value === 'number' && !Number.isInteger(value);
  }

  totalTracesVisible(show) {
    let totaltrace = this.getCurrentTotalTrace();
    if (!show) {
      totaltrace.visible = 'legendonly';
    } else {
      totaltrace.visible = true;
    }
  }

  getCurrentTotalTrace() {
    let traces = this.getTraceGroup(
      this.state.data,
      this.getCurrentVisibleTraceGroup()
    );
    for (let i = 0; i < traces.length; i++) {
      if (traces[i].name === this.TOTALNAME) {
        return traces[i];
      }
    }
  }

  hideTotalBasedOnRange() {
    let range = this.state.layout.xaxis.range;
    let startDate = new Date(range[0]);
    let endDate = new Date(range[1]);
    let months = this.monthDiff(startDate, endDate);
    if (months > 20) {
      this.totalTracesVisible(false);
    } else {
      this.totalTracesVisible(true);
    }
  }

  updateTotalTrace(alltraces, tracegroup) {
    let newTraces = [];
    let traces = this.getTraceGroup(alltraces, tracegroup);
    let totalTrace = [];

    // select none total traces
    for (let i = 0; i < traces.length; i++) {
      if (
        typeof traces[i].name === 'undefined' || // single trace charts contain no name
        !traces[i].name.includes(this.TOTALNAME)
      ) {
        newTraces.push(traces[i]);
      } else {
        totalTrace = traces[i];
      }
    }
    // calculate new total traces

    let newTotalTrace = this.generateTotalTrace(newTraces);
    // update old total trace
    totalTrace.y = newTotalTrace.y;
    totalTrace.text = newTotalTrace.text;
  }

  // Formating Methods
  formatMonthTraces(traces) {
    for (let i = 0; i < traces.length; i++) {
      traces[i].type = 'bar';
      traces[i].marker = [];
      traces[i].marker.color = traces[i].fillcolor;
    }
  }

  formatChangeTraces(traces) {
    for (let i = 0; i < traces.length; i++) {
      traces[i].type = 'bar';
      traces[i].marker = [];
      traces[i].marker.color = traces[i].fillcolor;
    }
  }

  formatYearlyTraces(traces) {
    for (let i = 0; i < traces.length; i++) {
      traces[i].type = 'bar';
      traces[i].marker = [];
      traces[i].marker.color = traces[i].fillcolor;
    }
  }

  formatTotalTrace(trace) {
    trace.type = 'scatter';
    trace.mode = 'text';
    trace.textposition = 'top';
    trace.hoverinfo = 'none';
    trace.textfont = {
      size: 12
    };
  }

  // Initialization
  // Creats out of time series charts a chart with changes, yearly views and total traces
  enrichChart(traces) {
    this.cleanTraces(traces);
    this.convertToPlotlyFriendlyXValues(traces);
    this.convertToAbsolutTraces(traces);

    let changeTraces = JSON.parse(JSON.stringify(traces));
    this.generateChangeTraces(changeTraces);

    let yearlyTraces = JSON.parse(JSON.stringify(traces));
    this.generateYearlyTraces(yearlyTraces, this.props.containsOverallValues);

    // Just grouping the first traces together with legendgroup hack
    let monthlyTraces = this.convertToMonthlyTraces(traces);

    let totalTrace = this.generateTotalTrace(monthlyTraces);
    let totalChangeTrace = this.generateTotalTrace(changeTraces);
    let totalYearlyTrace = this.generateTotalTrace(
      yearlyTraces,
      this.props.containsOverallValues
    );

    // Set how the traces should look like
    this.formatMonthTraces(monthlyTraces);
    this.formatChangeTraces(changeTraces);
    this.formatYearlyTraces(yearlyTraces);
    this.formatTotalTrace(totalTrace);
    this.formatTotalTrace(totalChangeTrace);
    this.formatTotalTrace(totalYearlyTrace);

    // Combine Totaltraces
    monthlyTraces.push(totalTrace);
    changeTraces.push(totalChangeTrace);
    yearlyTraces.push(totalYearlyTrace);

    // Set initial visbility
    this.showTraces(monthlyTraces);
    this.hideTraces(changeTraces);
    this.hideTraces(yearlyTraces);

    // Combine everything together
    let allTraces = monthlyTraces.concat(changeTraces, yearlyTraces);
    return allTraces;
  }

  calculateStartRange(traces) {
    // Set default Range to be the last 6 months
    // TODO Currently last month cut
    let startDate = new Date();
    if (traces[0].x.length >= 13) {
      startDate = new Date(traces[0].x[traces[0].x.length - 13]);
    } else {
      startDate = new Date(traces[0].x[0]);
    }
    startDate.setDate(startDate.getDate() - 15);
    let startDateString = startDate.toISOString().split('T')[0];

    let endDate = new Date(traces[0].x[traces[0].x.length - 1]);
    endDate.setDate(endDate.getDate() + 16);
    let endDateString = endDate.toISOString().split('T')[0];

    let monthlyRange = [startDateString, endDateString];
    return monthlyRange;
  }

  // generate the arrays for the visibility setting in update menues
  generateVisibilityArrays(traces, mode) {
    let visibles = [];
    // Normal visible
    if (mode === 'normal') {
      for (let i = 0; i < traces.length; i++) {
        if (
          typeof traces[i].legendgroup === 'undefined' ||
          traces[i].legendgroup.includes('MONTH')
        ) {
          visibles.push(true);
        } else {
          visibles.push(false);
        }
      }
    }
    // Change visible
    else if (mode === 'change') {
      for (let i = 0; i < traces.length; i++) {
        if (
          typeof traces[i].legendgroup !== 'undefined' &&
          traces[i].legendgroup.includes('CHANGE')
        ) {
          visibles.push(true);
        } else {
          visibles.push(false);
        }
      }
    }
    // Yearly visible
    else if (mode === 'yearly') {
      for (let i = 0; i < traces.length; i++) {
        if (
          typeof traces[i].legendgroup !== 'undefined' &&
          traces[i].legendgroup.includes('YEAR')
        ) {
          visibles.push(true);
        } else {
          visibles.push(false);
        }
      }
    }
    return visibles;
  }

  createRangeSelector() {
    const rangeselector = {
      buttons: [
        {
          step: 'month',
          stepmode: 'backward',
          count: 13,
          label: 'letztes Jahr'
        },
        {
          step: 'all',
          label: 'Historie'
        }
        // ,
        // {
        //   step: 'month',
        //   stepmode: 'backward',
        //   count: 6,
        //   label: '6 Monate'
        // }
      ],
      xanchor: 'left',
      mode: 'dropdown',
      x: 0,
      y: 1.035,
      bgcolor: 'white',
      activecolor: '#f7f7f7',
      font: {
        size: 12
      }
    };
    return rangeselector;
  }

  creatUpdateMenues(
    visiblesAbsolute,
    visiblesChanges,
    visiblesYears,
    monthlyRange
  ) {
    // Menu to change visualization style
    const updatemenus = [
      {
        buttons: [
          {
            args: [
              { visible: visiblesAbsolute },
              {
                xaxis: {
                  range: monthlyRange,
                  rangeselector: this.createRangeSelector()
                },
                yaxis: {
                  autorange: true
                }
              }
            ],
            label: 'Aggregation Monat',
            method: 'update'
          },
          {
            args: [
              { visible: visiblesChanges },
              {
                xaxis: {
                  range: monthlyRange,
                  rangeselector: this.createRangeSelector()
                },
                yaxis: {
                  autorange: true
                }
              }
            ],
            label: 'Veränderung zum Vormonat',
            method: 'update'
          },
          {
            args: [
              { visible: visiblesYears },
              {
                xaxis: { autorange: true },
                yaxis: {
                  autorange: true
                }
              }
            ],
            label: 'Aggregation Jahr',
            method: 'update'
          }
        ],
        direction: 'down',
        pad: { r: 20, t: 10 },
        showactive: true,
        type: 'dropdown',
        x: 0.3,
        xanchor: 'left',
        y: 1.14,
        yanchor: 'top',
        borderwidth: 0,
        //bgcolor: '#f7f7f7',
        //activecolor: '#f7f7f7',
        font: {
          size: 12
        }
      }
    ];
    return updatemenus;
  }

  styleLayout(layout) {
    // Styling
    layout.font.family = 'latoregular';
    layout.font.color = '#6D7B80';
    layout.autosize = true;
    layout.width = undefined;
    layout.height = undefined;
    layout.line = { dash: 'dot' };
    if (layout.legend) {
      if (layout.legend.font) {
        layout.legend.font.family = 'latoregular';
        layout.legend.font.color = '#42515A';
      } else {
        layout.legend = {
          font: {
            family: 'latoregular',
            color: '#42515A'
          }
        };
      }
    }
    layout.annotations &&
      layout.annotations.forEach(annotation => {
        if (!annotation) {
          return;
        }
        if (!annotation.font) {
          annotation.font = {
            family: 'latoblack',
            color: '#42515A',
            size: 12
          };
        } else {
          annotation.font.family = 'latoblack';
          annotation.font.color = '#42515A';
          annotation.font.size = 12;
        }
        // HACK to move low x-axis titles (rendered as annotations) in faceted charts below labels
        // This happens in faceted charts like in evo
        // This only works for a specific screen size
        if (annotation.x < 0) {
          annotation.x = annotation.x - 0.015;
        }
        // HACK to move low x-axis titles (rendered as annotations) in faceted charts below labels
        if (annotation.y < 0.5) {
          annotation.y = annotation.y - 0.05;
        } else {
          annotation.y = annotation.y + 0.01;
        }
      });

    layout.xaxis.color = '#6D7B80';
    if (layout.xaxis.title && typeof layout.xaxis.title === 'object') {
      if (layout.xaxis.title.font !== undefined) {
        layout.xaxis.title.font.family = 'latoblack';
        layout.xaxis.title.font.size = 12;

      } else {
        layout.xaxis.title.font = {
          family: 'latoregular',
          color: '#42515A',
          size: 12
        };
      }
    } else if (layout.xaxis.titlefont !== undefined) {
      layout.xaxis.titlefont.family = 'latoblack';
      layout.xaxis.titlefont.color = '#42515A';
      layout.xaxis.titlefont.size = 12;
    }
    // TODO Fix: Somehow this is lost in the on update / button clicked handling
    if (!layout.xaxis.tickfont) {
      layout.xaxis.tickfont = {};
    }
    layout.xaxis.tickfont.family = 'latoregular';
    layout.xaxis.tickfont.color = '#6D7B80';
    layout.xaxis.tickfont.size = 12;
    layout.yaxis.color = '#6D7B80';

    if (typeof layout.yaxis.title !== undefined && typeof layout.yaxis.title === 'object') {
      layout.yaxis.title.standoff = 20;
      if (layout.yaxis.title.font && typeof layout.yaxis.title.font !== undefined) {
        layout.yaxis.title.font.family = 'latoblack';
        layout.yaxis.title.font.size = 12;
      } else {
        layout.yaxis.title.font = {
          family: 'latoblack',
          color: '#42515A',
          size: 12,
        };
      }
    } else if (layout.yaxis.titlefont !== undefined) {
      layout.yaxis.titlefont.family = 'latoblack';
      layout.yaxis.titlefont.color = '#42515A';
      layout.yaxis.titlefont.size = 12;
    }
    layout.yaxis.ticks = '';
    if (!layout.yaxis.tickfont) {
      layout.yaxis.tickfont = {};
    }
    layout.yaxis.tickfont.family = 'latoregular';
    layout.yaxis.tickfont.color = '#6D7B80';
    layout.yaxis.tickfont.size = 12;
    for (let i = 1; i < 11; i++) {
      if (layout[`xaxis${i}`]) {
        layout[`xaxis${i}`].color = '#6D7B80';
        layout[`xaxis${i}`].tickfont.family = 'latoregular';
        layout[`xaxis${i}`].tickfont.color = '#6D7B80';
      }
      if (layout[`yaxis${i}`]) {
        layout[`yaxis${i}`].color = '#6D7B80';
        layout[`yaxis${i}`].tickfont.family = 'latoregular';
        layout[`yaxis${i}`].tickfont.color = '#6D7B80';
      }
    }
    layout.yaxis.gridcolor = '#f0f2f2';
    layout.yaxis.zeroline = true;
    if (layout.yaxis.ticktext) {
      layout.yaxis.ticktext = layout.yaxis.ticktext.map(
        ticktext => '   ' + ticktext
      );
    }
    layout.yaxis.zerolinecolor = '#6D7B80';
    layout.margin.l = 90;
    layout.margin.pad = 5;
  }

  initializeChart() {
    const data = JSON.parse(this.props.data) || {};

    let layout = Object.assign({
      margin: {},
      font: {},
      xaxis: {},
      yaxis: {},
      legend: {},
    }, data.layout);

    layout.datarevision = this.state.revision;
    layout.hiddenlabes = [];

    layout.xaxis.rangeselector = this.createRangeSelector();

    let tracesToShow = data.data || [];
    normalizeBarTooltips(tracesToShow);
    if (this.props.showAsTimeseries) {
      tracesToShow = this.enrichChart(data.data);
      normalizeBarTooltips(tracesToShow);
      layout.xaxis.range = this.calculateStartRange(data.data);
      layout.updatemenus = this.creatUpdateMenues(
        this.generateVisibilityArrays(tracesToShow, 'normal'),
        this.generateVisibilityArrays(tracesToShow, 'change'),
        this.generateVisibilityArrays(tracesToShow, 'yearly'),
        this.calculateStartRange(tracesToShow)
      );

      // Formating
      delete layout.xaxis.ticktext;
      delete layout.xaxis.tickvals;
      delete layout.xaxis.tickmode;
      delete layout.xaxis.categoryarray;
      delete layout.xaxis.categoryorder;
      delete layout.xaxis.type;

      //remove to let plotly do the axis   magic
      delete layout.yaxis.range;
      delete layout.yaxis.ticktext;
      delete layout.yaxis.tickvals;
      delete layout.yaxis.tickmode;
      delete layout.yaxis.categoryarray;
      delete layout.yaxis.categoryorder;

      layout.yaxis.autorange = true;

      layout.scrollZoom = true;
    }

    //  layout.yaxis.fixedrange = true;

    this.styleLayout(layout);

    this.setState({ data: tracesToShow });
    this.setState({ layout: layout });
  }

  initalizeConfig() {
    const config = {
      modeBarButtonsToRemove: [
        'toImage',
        'sendDataToCloud',
        'zoomIn2d',
        'zoomOut2d',
        //'autoScale2d',
        'toggleSpikelines',
        'lasso2d',
        'select2d'
      ],
      displaylogo: false,
      responsive: true,
      locale: 'de',
      modeBarButtonsToAdd: [
        {
          name: 'Download Chart',
          //icon: cloudicon,
          icon: plotly.Icons.disk,
          click: this.handleCSVClick
        }
      ]
    };
    this.setState({ config: config });
  }

  // Turning Json data specific for plots into Download
  createPlotDownloadData(datain, layout) {
    console.log(datain);
    let data = JSON.parse(JSON.stringify(datain));
    let returnData = [];
    if (this.props.showAsTimeseries) {
      data.forEach(row => {
        row.y.unshift(row.name);
        returnData.push(row.y);
      });
      data[0].x.unshift('Datum');
      returnData.unshift(data[0].x);
    } else if (data.length === 1 && data[0].orientation === 'v') {
      returnData.push(layout.xaxis.ticktext.map(x => x.trim()));
      returnData.push(
        data[0].y.map(y =>
          new Intl.NumberFormat('de-DE', {
            maximumSignificantDigits: 4
          }).format(y)
        )
      );
    } else if (data.length > 1 && data[0].orientation === 'v') {
      // NOT Working yet. See https://ben-energy.atlassian.net/browse/CIT-431
      let data0 = data[0].text.map(x => x.split('<br />'));
      console.log(data0);
      returnData.push(
        data0.map(x => { return x.map(y => y.split(': ')[0]); })[0]
      );
      data.forEach(element => {
        let dataLine = element.text.map(x => x.split('<br />'));
        console.log(dataLine);
        let dataValues = dataLine.map(x => { return x.map(y => y.split(': ')[1]); });
        console.log(dataValues);
        dataValues.forEach(row => {
          returnData.push(row);
        });
      });
    } else if (data.length === 1 && data[0].orientation === 'h') {
      returnData.push(layout.yaxis.ticktext.map(y => y.trim()));
      returnData.push(
        data[0].x.map(x =>
          new Intl.NumberFormat('de-DE', {
            maximumSignificantDigits: 4
          }).format(x)
        )
      );
    } else if (data.length === 1 && data[0].type === 'scatter') {
      // Parsing Bubble Charts from the tooltip
      // Format is - N: 18.454 <br />Risikosegment: 1 <br />Kundenwert: D", "N: 57.786 <br />Risikosegment: 1 <br />Kundenwert: C ...,
      let data2 = data[0].text.map(x => x.split('<br />'));
      returnData.push(
        data2.map(x => { return x.map(y => y.split(': ')[0]); })[0]);
      let data3 = data2.map(x => { return x.map(y => y.split(': ')[1]); });
      data3.forEach(row => {
        returnData.push(row);
      });
    }
    console.log(returnData);
    return returnData;
  }

  // Support function
  convertArrayOfObjectsToCSV(args) {
    let result, ctr, keys, columnDelimiter, lineDelimiter, data;

    data = args.data || null;
    if (data == null || !data.length) {
      return null;
    }

    columnDelimiter = args.columnDelimiter || ';';
    lineDelimiter = args.lineDelimiter || '\n';

    keys = Object.keys(data[0]);
    result = '';

    data.forEach(function (item) {
      ctr = 0;
      keys.forEach(function (key) {
        if (ctr > 0) result += columnDelimiter;
        if (key in item) {
          result += item[key];
        } else {
          result += '';
        }

        ctr++;
      });
      result += lineDelimiter;
    });
    return result;
  }

  handleCSVClick() {
    let content = this.createPlotDownloadData(
      this.state.data,
      this.state.layout
    );
    let csv = this.convertArrayOfObjectsToCSV({ data: content });
    if (!csv) return;
    if (!csv.match(/^data:text\/csv/i)) {
      csv = 'data:text/csv;charset=utf-8,\uFEFF' + csv;
    }
    let encodedUri = encodeURI(csv);
    let link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      this.props.title + ' ' + this.props.lastUpdated + '.csv'
    );
    document.body.appendChild(link);
    link.click();
  }

  selectionURL() {
    // console.log(this.props.data.base_attribute);
    return '/api/v1/componentdownload/814/Sparte/Strom/2018-09';
  }

  render() {
    const { title, info } = this.props;
    const Plot = createPlotlyComponent(plotly);
    return (
      <StyledPlotComponent columns={this.props.columns}>
        <ComponentTitle
          title={title}
          info={info}
          lastUpdated={this.props.lastUpdated}
        />
        <StyledPlotWrapper>
          <Plot
            data={this.state.data}
            layout={this.state.layout}
            //frames={this.state.frames}
            config={this.state.config}
            revision={this.state.revision}
            //onInitialized={figure => this.setState(figure)}
            //useResizeHandler={true} // not working with dynamic update meachnism
            onUpdate={figure => this.handleUpdate(figure)} // Called by clicking on legend items and range changes
            onButtonClicked={e => this.handleButtonClicked(e)} // if trace group to show is changed via updatemenues
            //onRelayout={e => this.handleRelayout(e)} //layout attributes change. Alternative to onButton clicked. But then data is missing
            //onRestyle={e => this.handleOnRestyle(e)} // data changes
            //onRedraw={e => console.log('onRedraw' + e)}
            //onAfterPlot={e => console.log(e)}
            //onClick={e => this.handleClick(e)} // Disabled for now TODO finish click download
            style={{
              width: '100%',
              height: `${this.props.heightInPx ? `${this.props.heightInPx}px` : 'auto'
                }`
            }}
          />
          <UpdatedDate>{`letztes Update: ${this.props.lastUpdated
            }`}</UpdatedDate>
        </StyledPlotWrapper>
      </StyledPlotComponent>
    );
  }
}

export default PlotComponent;
